import React, { useState } from 'react';
import { Button } from '../ui/button';
import { GoldIcon, CheckIcon, ArrowRightIcon } from '../icons/ElchmeIcons';
import { Download, Loader2, Sparkles, BookOpen, Heart, Feather } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ChapterSummaryGenerator - Creates a beautiful AI-enhanced journal PDF of user's chapter work
 */
export default function ChapterSummaryGenerator({ 
  chapterNumber, 
  chapterTitle,
  responses = {},
  exercises = [],
  userId,
  onClose 
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [summaryContent, setSummaryContent] = useState(null);
  const [aiNarrative, setAiNarrative] = useState('');

  // Inspirational quotes for the journal
  const inspirationalQuotes = [
    { quote: "What I want in my life is compassion, a flow between myself and others based on a mutual giving from the heart.", author: "Marshall B. Rosenberg" },
    { quote: "Every criticism, judgment, diagnosis, and expression of anger is the tragic expression of an unmet need.", author: "Marshall B. Rosenberg" },
    { quote: "When we focus on clarifying what is being observed, felt, and needed rather than on diagnosing and judging, we discover the depth of our own compassion.", author: "Marshall B. Rosenberg" },
    { quote: "The more we choose to hear their words as a gift, the deeper our connecting.", author: "Marshall B. Rosenberg" },
    { quote: "Behind intimidating messages are simply people appealing to us to meet their needs.", author: "Marshall B. Rosenberg" },
  ];

  const getRandomQuote = () => {
    return inspirationalQuotes[Math.floor(Math.random() * inspirationalQuotes.length)];
  };

  const generateSummary = async () => {
    setIsGenerating(true);
    
    try {
      // Get AI-generated narrative if user has responses
      let narrative = '';
      if (userId && Object.keys(responses).length > 0) {
        try {
          const aiResponse = await fetch(`${BACKEND_URL}/api/workbook/summary/${userId}`);
          if (aiResponse.ok) {
            const aiData = await aiResponse.json();
            narrative = aiData.summary || '';
          }
        } catch (e) {
          console.log('AI summary not available, using fallback');
        }
      }

      // Compile all user responses
      const summary = {
        header: `Chapter ${chapterNumber}: ${chapterTitle}`,
        date: new Date().toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        time: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }),
        sections: [],
        quote: getRandomQuote(),
        totalExercises: exercises.length,
        completedExercises: 0
      };

      // Process each exercise
      exercises.forEach((exercise, index) => {
        const exerciseResponses = responses[exercise.id] || {};
        if (Object.keys(exerciseResponses).length > 0) {
          summary.completedExercises++;
          summary.sections.push({
            title: exercise.title,
            page: exercise.page || '',
            content: exerciseResponses
          });
        }
      });

      // Generate fallback narrative if AI didn't provide one
      if (!narrative) {
        const completionRate = Math.round((summary.completedExercises / summary.totalExercises) * 100);
        narrative = `Your journey through ${chapterTitle} reflects a growing awareness of compassionate communication. You've thoughtfully engaged with ${summary.completedExercises} of ${summary.totalExercises} exercises, showing dedication to understanding both yourself and others more deeply. Each response you've written is a step toward more authentic connection. Continue nurturing this practice—the seeds of compassion you're planting today will blossom in all your relationships.`;
      }

      setAiNarrative(narrative);
      setSummaryContent(summary);
      setSummaryGenerated(true);
      toast.success('Your journal entry is ready!');
    } catch (error) {
      console.error('Summary generation error:', error);
      toast.error('Failed to generate summary. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPDF = () => {
    if (!summaryContent) return;

    // Create beautiful journal-style HTML content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${summaryContent.header} - My NVC Journal</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@400;500;600&display=swap');
          
          * { box-sizing: border-box; margin: 0; padding: 0; }
          
          body {
            font-family: 'Crimson Text', Georgia, serif;
            background: #faf8f5;
            color: #3d3929;
            padding: 0;
            min-height: 100vh;
            line-height: 1.8;
          }
          
          .page {
            max-width: 700px;
            margin: 0 auto;
            padding: 60px 50px;
            background: #faf8f5;
          }
          
          /* Decorative header */
          .header-decoration {
            text-align: center;
            margin-bottom: 20px;
          }
          
          .header-decoration svg {
            width: 60px;
            height: 60px;
            color: #8B7355;
          }
          
          .flourish {
            text-align: center;
            color: #c4b7a6;
            font-size: 24px;
            letter-spacing: 8px;
            margin: 15px 0;
          }
          
          h1 {
            font-family: 'Playfair Display', Georgia, serif;
            font-size: 2.2em;
            font-weight: 600;
            color: #5d4e37;
            text-align: center;
            margin-bottom: 8px;
            letter-spacing: 1px;
          }
          
          .subtitle {
            font-family: 'Montserrat', sans-serif;
            text-align: center;
            color: #8B7355;
            font-size: 0.85em;
            text-transform: uppercase;
            letter-spacing: 3px;
            margin-bottom: 10px;
          }
          
          .date-line {
            text-align: center;
            color: #a89f91;
            font-style: italic;
            font-size: 0.95em;
            margin-bottom: 40px;
            padding-bottom: 30px;
            border-bottom: 1px solid #e8e2d9;
          }
          
          /* Quote section */
          .quote-section {
            background: linear-gradient(135deg, #f5f1eb 0%, #ede8e0 100%);
            border-left: 3px solid #c4a77d;
            padding: 25px 30px;
            margin: 30px 0;
            border-radius: 0 8px 8px 0;
          }
          
          .quote-text {
            font-style: italic;
            font-size: 1.1em;
            color: #5d4e37;
            line-height: 1.7;
            margin-bottom: 12px;
          }
          
          .quote-author {
            font-family: 'Montserrat', sans-serif;
            font-size: 0.8em;
            color: #8B7355;
            text-align: right;
          }
          
          /* AI Narrative section */
          .narrative-section {
            background: #fff;
            border: 1px solid #e8e2d9;
            border-radius: 12px;
            padding: 30px;
            margin: 35px 0;
            box-shadow: 0 2px 8px rgba(93, 78, 55, 0.08);
          }
          
          .narrative-header {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 15px;
            padding-bottom: 15px;
            border-bottom: 1px dashed #e8e2d9;
          }
          
          .narrative-icon {
            width: 24px;
            height: 24px;
            color: #c4a77d;
          }
          
          .narrative-title {
            font-family: 'Montserrat', sans-serif;
            font-size: 0.85em;
            text-transform: uppercase;
            letter-spacing: 2px;
            color: #8B7355;
          }
          
          .narrative-text {
            font-size: 1.05em;
            color: #4a4439;
            line-height: 1.9;
          }
          
          /* Exercise sections */
          .section {
            margin: 35px 0;
            page-break-inside: avoid;
          }
          
          .section-title {
            font-family: 'Playfair Display', Georgia, serif;
            color: #5d4e37;
            font-size: 1.25em;
            font-weight: 500;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .section-title::before {
            content: '✦';
            color: #c4a77d;
            font-size: 0.8em;
          }
          
          .section-page {
            font-family: 'Montserrat', sans-serif;
            color: #a89f91;
            font-size: 0.75em;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 15px;
          }
          
          .response {
            background: #fdfcfa;
            border: 1px solid #ebe6de;
            padding: 20px;
            margin: 15px 0;
            border-radius: 8px;
          }
          
          .response-label {
            font-family: 'Montserrat', sans-serif;
            color: #8B7355;
            font-size: 0.75em;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }
          
          .response-text {
            color: #4a4439;
            font-size: 1em;
            line-height: 1.8;
          }
          
          /* Progress indicator */
          .progress-section {
            text-align: center;
            margin: 40px 0 30px;
            padding: 20px;
            background: linear-gradient(135deg, #f9f7f4 0%, #f5f1eb 100%);
            border-radius: 12px;
          }
          
          .progress-label {
            font-family: 'Montserrat', sans-serif;
            font-size: 0.75em;
            text-transform: uppercase;
            letter-spacing: 2px;
            color: #8B7355;
            margin-bottom: 8px;
          }
          
          .progress-value {
            font-family: 'Playfair Display', Georgia, serif;
            font-size: 1.8em;
            color: #5d4e37;
          }
          
          /* Footer */
          .footer {
            text-align: center;
            margin-top: 50px;
            padding-top: 30px;
            border-top: 1px solid #e8e2d9;
          }
          
          .footer-flourish {
            color: #c4b7a6;
            font-size: 18px;
            letter-spacing: 6px;
            margin-bottom: 15px;
          }
          
          .footer-text {
            font-family: 'Montserrat', sans-serif;
            color: #a89f91;
            font-size: 0.7em;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          
          .footer-brand {
            font-family: 'Playfair Display', Georgia, serif;
            color: #8B7355;
            font-size: 1.1em;
            margin-top: 8px;
          }
          
          /* Print styles */
          @media print {
            body { background: white; }
            .page { padding: 40px 30px; box-shadow: none; }
            .narrative-section { box-shadow: none; }
          }
          
          /* Decorative elements */
          .leaf-decoration {
            text-align: center;
            margin: 20px 0;
            color: #c4b7a6;
          }
        </style>
      </head>
      <body>
        <div class="page">
          <!-- Decorative Header -->
          <div class="header-decoration">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
            </svg>
          </div>
          
          <p class="flourish">✦ ✦ ✦</p>
          
          <h1>${summaryContent.header}</h1>
          <p class="subtitle">My NVC Journey • Personal Reflection</p>
          <p class="date-line">${summaryContent.date} at ${summaryContent.time}</p>
          
          <!-- Inspirational Quote -->
          <div class="quote-section">
            <p class="quote-text">"${summaryContent.quote.quote}"</p>
            <p class="quote-author">— ${summaryContent.quote.author}</p>
          </div>
          
          <!-- AI-Generated Narrative -->
          <div class="narrative-section">
            <div class="narrative-header">
              <svg class="narrative-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/>
                <line x1="16" y1="8" x2="2" y2="22"/>
                <line x1="17.5" y1="15" x2="9" y2="15"/>
              </svg>
              <span class="narrative-title">Your Journey Reflection</span>
            </div>
            <p class="narrative-text">${aiNarrative}</p>
          </div>
          
          <!-- Progress Indicator -->
          <div class="progress-section">
            <p class="progress-label">Exercises Completed</p>
            <p class="progress-value">${summaryContent.completedExercises} of ${summaryContent.totalExercises}</p>
          </div>
          
          <div class="leaf-decoration">❧</div>
          
          <!-- Exercise Responses -->
          ${summaryContent.sections.map(section => `
            <div class="section">
              <h2 class="section-title">${section.title}</h2>
              ${section.page ? `<p class="section-page">Workbook Page ${section.page}</p>` : ''}
              ${Object.entries(section.content).map(([key, value]) => `
                <div class="response">
                  ${typeof value === 'object' ? 
                    Object.entries(value).map(([k, v]) => `
                      <p class="response-label">${k}</p>
                      <p class="response-text">${v || '—'}</p>
                    `).join('') :
                    `<p class="response-text">${value || '—'}</p>`
                  }
                </div>
              `).join('')}
            </div>
          `).join('')}
          
          <!-- Footer -->
          <div class="footer">
            <p class="footer-flourish">✦ ✦ ✦</p>
            <p class="footer-text">Generated from The Belonging E-Book</p>
            <p class="footer-brand">The Belonging Sessions</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);

    toast.success('Journal ready! Use your browser\'s print dialog to save as PDF.');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="summary-generator-modal">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/50 rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner border border-amber-200/50">
            <Feather className="w-10 h-10 text-amber-700" />
          </div>
          <h2 className="text-2xl font-bold text-amber-900 mb-2 font-serif">Create Your Journal Entry</h2>
          <p className="text-amber-700/80">
            Generate a beautiful keepsake of your Chapter {chapterNumber} reflections
          </p>
        </div>

        {/* Content */}
        {!summaryGenerated ? (
          <div className="space-y-6">
            <div className="bg-white/60 border border-amber-200/50 rounded-xl p-5">
              <h3 className="text-amber-800 font-medium mb-3 flex items-center gap-2 font-serif">
                <Sparkles className="w-4 h-4 text-amber-600" />
                What's Included
              </h3>
              <ul className="space-y-3 text-sm text-amber-900/80">
                <li className="flex items-center gap-3">
                  <Heart className="w-4 h-4 text-rose-400 flex-shrink-0" />
                  <span>AI-crafted narrative of your journey</span>
                </li>
                <li className="flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span>All your exercise responses beautifully formatted</span>
                </li>
                <li className="flex items-center gap-3">
                  <Feather className="w-4 h-4 text-amber-700 flex-shrink-0" />
                  <span>Inspirational NVC quote for reflection</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckIcon size={16} color="#b45309" />
                  <span>Elegant journal-style PDF you can print or save</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                Maybe Later
              </Button>
              <Button
                onClick={generateSummary}
                disabled={isGenerating}
                className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-semibold shadow-lg shadow-amber-500/25"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Journal
                    <ArrowRightIcon size={16} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200/50 rounded-xl p-5 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckIcon size={24} color="#059669" />
              </div>
              <p className="text-emerald-800 font-medium font-serif">Your Journal is Ready!</p>
              <p className="text-emerald-600/80 text-sm mt-1">
                A beautiful keepsake of your NVC journey awaits
              </p>
            </div>

            {/* Preview of AI narrative */}
            {aiNarrative && (
              <div className="bg-white/60 border border-amber-200/50 rounded-xl p-4">
                <p className="text-xs text-amber-600 uppercase tracking-wider mb-2 font-medium">Preview</p>
                <p className="text-amber-900/80 text-sm italic line-clamp-3">{aiNarrative}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                Close
              </Button>
              <Button
                onClick={downloadPDF}
                className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold shadow-lg shadow-emerald-500/25"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Journal
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
